import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { PageLoaderComponent } from '@app-ui';
import { AppLoaderService } from './services';
import { AsyncPipe, NgIf } from '@angular/common';
import {
  AppTranslationLoader,
  BaseComponent,
  isSystemSuperAdminFromToken,
  Organization,
  ProfileService,
  StorageService,
  SYSTEM_SUPER_ADMIN_HOME,
} from '@app-utils';
import { takeUntil } from 'rxjs';
import { OrganizationSwitchModalComponent } from '@app-core';
import { TranslateModule } from '@ngx-translate/core';

/**
 * App Component
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
  standalone: true,
  imports: [
    RouterOutlet,
    PageLoaderComponent,
    NgIf,
    AsyncPipe,
    OrganizationSwitchModalComponent,
    TranslateModule,
  ],
  providers: [AppTranslationLoader],
})
export class AppComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  private readonly _router = inject(Router);
  private readonly _storage = inject(StorageService);
  readonly profile = inject(ProfileService);
  readonly loader = inject(AppLoaderService);
  readonly langLoader = inject(AppTranslationLoader);

  showOrgSelection = false;

  orgList: Organization[] = [];

  ngOnInit(): void {
    this._router.events.pipe(takeUntil(this._destroy$)).subscribe((e) => {
      if (e instanceof NavigationEnd) {
        if (this._router.url === '/') {
          this._checkSelectedOrg();
        }
        this._checkIfToShowSelection();
      }
    });
  }

  ngAfterViewInit(): void {
    this.profile.profile$.pipe(takeUntil(this._destroy$)).subscribe((res) => {
      if (!res) return;
      this._getOrgList();
      if (this._router.url === '/' || this._router.url === '') {
        this._checkSelectedOrg();
      }
    });
  }

  private _checkSelectedOrg(): void {
    if (isSystemSuperAdminFromToken(this._storage)) {
      void this._router.navigateByUrl(SYSTEM_SUPER_ADMIN_HOME);
      return;
    }

    const orgList = this.profile.latestOrganizationList();
    if (!orgList.length) return;
    const org = this.profile.latestOrganization();
    if (org) {
      void this._router.navigate(['org', org.id]);
      return;
    }
    if (orgList.length === 1) {
      this.profile.setOrganization(orgList[0]);
      void this._router.navigate(['org', orgList[0].id]);
      return;
    }
    this.showOrgSelection = true;
  }

  private _getOrgList(): void {
    this.profile.organizationList$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        this.orgList = res;
        // if (this.orgList.length === 1) {
        //   void this._router.navigate(['org', this.orgList[0].id]);
        //   return;
        // }
        this._checkIfToShowSelection();
      });
  }

  private _checkIfToShowSelection(): void {
    this.showOrgSelection =
      !this.profile.latestOrganization() &&
      this.orgList.length >= 2 &&
      this._isNormalRoute;
  }

  private get _isNormalRoute(): boolean {
    // These callbacks know their organisation (from the connect that started them) and pick it
    // themselves. Asking here would cover the page, and choosing would leave it.
    return (
      !this._router.url.includes('/invitation/') &&
      !this._router.url.startsWith('/social/callback') &&
      !this._router.url.startsWith('/email/callback')
    );
  }

  organizationChange(data: Organization): void {
    this.showOrgSelection = false;
    void this._router.navigate(['org', data.id]);
  }
}
